// frontend/src/components/athleteEditor/MeasurablesCard.jsx
// Flat measurables card (redesign screen 1a) — re-homes MeasurablesEditor's
// save/verify brain (now in useMeasurableSaves) into the new visual shell:
// one card, category sections with a verified-count chip, 5-col field grid,
// inline seal buttons. Port is faithful to MeasurablesEditor.jsx (sport->slug
// resolution, column-backed catalog filter, BMI, gear fields) — see
// .superpowers/sdd/task-4-brief.md.
import React from 'react';
import { useCatalog } from '../../context/CatalogContext';
import { computeBmi } from '../../lib/athleteDetail';
import { verificationCounts } from '../../lib/athleteEditorModel';
import EditorField from './EditorField';
import SealButton from './SealButton';
import useMeasurableSaves from './useMeasurableSaves';
import { formatMeasurable } from '../../lib/publicProfile';
import { CheckIcon, VerifiedSeal } from './staffProfileIcons';

// Exported so MeasurablesLedger (Task 6) renders the identical gear option
// lists instead of duplicating them.
export const VENDOR_OPTIONS = ['Nike', 'Adidas', 'New Balance', 'Under Armour', 'Jordan', 'Mizuno', 'Asics', 'Hoka', 'Brooks', 'Other'];
export const JERSEY_OPTIONS = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];
export const HAND_PREF_OPTIONS = ['Right', 'Left', 'Ambidextrous'];
export const FOOT_PREF_OPTIONS = ['Right', 'Left', 'Both'];
export const GEAR_SELECT_FIELDS = [
  { name: 'shoe_vendor', label: 'Shoe Vendor', options: VENDOR_OPTIONS },
  { name: 'cleat_vendor', label: 'Cleat Vendor', options: VENDOR_OPTIONS },
  { name: 'jersey_size', label: 'Jersey Size', options: JERSEY_OPTIONS },
  { name: 'hand_preference', label: 'Hand Preference', options: HAND_PREF_OPTIONS },
  { name: 'foot_preference', label: 'Foot Preference', options: FOOT_PREF_OPTIONS },
];

const hasValue = v => v !== null && v !== undefined && v !== '';

// Category display order override — Size leads (coaches scan height/weight
// first); remaining catalog categories keep their catalog order. Shared with
// MeasurablesLedger so the card and the verify ledger show the same order.
export const orderGroupsSizeFirst = (arr) => {
  const size = arr.filter(g => g.category === 'Size');
  const rest = arr.filter(g => g.category !== 'Size');
  return [...size, ...rest];
};

/**
 * Measurables as read-only tiles for the staff/partner read view (`.adm`
 * look). Same data, same catalog groups (groupedMeasurablesFor(sport slug),
 * column-backed filter, Size first, BMI, the cross-sport Gear section) and the
 * same verify call as MeasurablesCard via useMeasurableSaves. Values are not
 * edited here; that is Edit mode. Seals are controls only for perms.canVerify
 * (staff and partners, never a coach).
 */
export function MeasurablesTilesCard({ userId, profile, perms, apiUrl, getConfig, logs, onChange, onSaved }) {
  const catalog = useCatalog();
  const safe = profile || {};
  const { values, errors, toggleVerify, sealState, sealTitle } =
    useMeasurableSaves({ userId, profile, apiUrl, getConfig, logs, onChange, onSaved });
  const canVerify = !!(perms && perms.canVerify);

  const sportName = safe.sport || 'Football';
  const sport = catalog.sports.find(s => s.name === sportName) || {};
  const slug = sport.slug || 'football';

  const catalogGroups = orderGroupsSizeFirst(catalog.groupedMeasurablesFor(slug)
    .map(g => ({ ...g, measurables: g.measurables.filter(m => Object.prototype.hasOwnProperty.call(safe, m.key)) }))
    .filter(g => g.measurables.length > 0));
  const gearGroup = {
    category: 'Gear & preferences',
    measurables: [
      { key: 'shoe_size', label: 'Shoe Size', unit: null },
      ...GEAR_SELECT_FIELDS.map(f => ({ key: f.name, label: f.label, unit: null })),
    ],
  };
  const bmi = computeBmi(values.height_inches, values.weight_lbs);

  const renderTile = (m) => {
    const raw = values[m.key];
    const has = hasValue(raw);
    const seal = sealState(m.key);
    const verified = seal === 'verified';
    const mod = verified ? ' adm-mtile--verified' : (has ? '' : ' adm-mtile--empty');
    return (
      <div className={`adm-mtile${mod}`} key={m.key} data-testid={`mtile-${m.key}`}>
        <div className="adm-mtile__text">
          <div className="adm-mtile__label" title={m.label}>{m.label}</div>
          <div className={`adm-mtile__value${has ? '' : ' adm-mtile__value--empty'}`}>
            {has ? formatMeasurable(m.key, raw, m.unit) : '—'}
          </div>
          {errors[m.key] && <div className="adm-mtile__error" role="alert">{errors[m.key]}</div>}
        </div>
        {verified && canVerify && (
          <button
            type="button" className="adm-seal" title={sealTitle(m.key)}
            aria-label={`${m.label}: verified. Click to unverify`}
            onClick={() => toggleVerify(m.key)}
          >
            <CheckIcon />
          </button>
        )}
        {verified && !canVerify && <VerifiedSeal />}
        {seal === 'unverified' && canVerify && (
          <button
            type="button" className="adm-seal adm-seal--ghost" title={sealTitle(m.key)}
            aria-label={`${m.label}: not verified. Click to verify`}
            onClick={() => toggleVerify(m.key)}
          />
        )}
      </div>
    );
  };

  const renderGroup = (group) => {
    const keys = group.measurables.map(m => m.key);
    const c = verificationCounts(values, keys);
    return (
      <div className="adm-mgroup" key={group.category}>
        <div className="adm-mgroup__head">
          <span className="adm-field__label">{group.category}</span>
          <span className={`adm-mgroup__count${c.verified > 0 ? ' adm-mgroup__count--some' : ''}`}>
            {c.verified} of {keys.length} verified
          </span>
        </div>
        <div className="adm-mtiles">
          {group.measurables.map(renderTile)}
          {group.category === 'Size' && (
            <div className={`adm-mtile${bmi != null ? '' : ' adm-mtile--empty'}`} key="bmi">
              <div className="adm-mtile__text">
                <div className="adm-mtile__label" title="BMI (derived)">BMI · derived</div>
                <div className={`adm-mtile__value${bmi != null ? '' : ' adm-mtile__value--empty'}`}>{bmi != null ? bmi : '—'}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <section className="adm__card adm__card--flush">
      <div className="adm-card__head">
        <h2 className="adm-card__title">Measurables</h2>
        {canVerify && <span className="adm-card__meta">{sport.name || sportName} · tap a seal to verify</span>}
      </div>
      {catalogGroups.map(renderGroup)}
      {renderGroup(gearGroup)}
    </section>
  );
}

export default function MeasurablesCard({ userId, profile, perms, apiUrl, getConfig, logs, onChange, onSaved }) {
  const catalog = useCatalog();
  const safe = profile || {};
  const { values, setValues, errors, commitField, toggleVerify, fieldState, sealState, sealTitle } =
    useMeasurableSaves({ userId, profile, apiUrl, getConfig, logs, onChange, onSaved });

  const editable = !!(perms && perms.canEditValues);
  const canVerify = !!(perms && perms.canVerify);

  // Resolve the athlete's sport -> catalog slug the same way PlayerStatsPage.js
  // does, falling back to football for an unset/unrecognized sport.
  const sportName = safe.sport || 'Football';
  const slug = (catalog.sports.find(s => s.name === sportName) || {}).slug || 'football';

  // Only render catalog measurables backed by a flat AthleteProfile column —
  // see MeasurablesEditor.jsx's `groups` comment for why (save/verify
  // endpoints 400 on keyless measurables).
  const groups = orderGroupsSizeFirst(catalog.groupedMeasurablesFor(slug)
    .map(g => ({ ...g, measurables: g.measurables.filter(m => Object.prototype.hasOwnProperty.call(safe, m.key)) }))
    .filter(g => g.measurables.length > 0));

  const renderMeasurable = (m) => (
    <EditorField key={m.key} label={m.label} unit={m.unit} state={fieldState(m.key)}>
      <input
        className="ae-field__input"
        value={values[m.key] ?? ''}
        disabled={!editable}
        placeholder={hasValue(values[m.key]) ? '' : '—'}
        onChange={e => setValues(v => ({ ...v, [m.key]: e.target.value }))}
        onBlur={e => editable && commitField(m.key, e.target.value, false)}
      />
      {canVerify && <SealButton state={sealState(m.key)} title={sealTitle(m.key)} onToggle={() => toggleVerify(m.key)} />}
      {errors[m.key] && <div className="form-field__error">{errors[m.key]}</div>}
    </EditorField>
  );

  const bmi = computeBmi(values.height_inches, values.weight_lbs);

  return (
    <div className="ae-card">
      <div className="ae-card__head">
        <h2 className="ae-card__title">Measurables</h2>
        <span className="ae-card__hint">CLICK THE SEAL ON A FIELD TO VERIFY · SAVES ON BLUR</span>
      </div>
      {groups.map(group => {
        const keys = group.measurables.map(m => m.key);
        const c = verificationCounts(values, keys);
        const withVal = c.verified + c.unverified;
        return (
          <div className="ae-section" key={group.category}>
            <div className="ae-section__head">
              <span className="ae-section__name">{group.category}</span>
              <span className={`ae-section__count ${c.verified > 0 ? 'ae-section__count--some' : 'ae-section__count--none'}`}>
                {c.verified}/{withVal || group.measurables.length} VERIFIED
              </span>
            </div>
            <div className="ae-grid5">
              {group.measurables.map(renderMeasurable)}
              {group.category === 'Size' && (
                <EditorField label="BMI" unit="derived" state="derived">
                  <input className="ae-field__input" value={bmi != null ? bmi : '—'} disabled readOnly />
                </EditorField>
              )}
            </div>
          </div>
        );
      })}
      {/* Gear & Preferences — not catalog measurables, so they stay as an
          explicit, cross-sport section (POST .../profile, not update-stat). */}
      <div className="ae-section">
        <div className="ae-section__head"><span className="ae-section__name">Gear &amp; Preferences</span></div>
        <div className="ae-grid5">
          <EditorField label="Shoe Size" unit="US" state={fieldState('shoe_size')}>
            <input
              className="ae-field__input"
              type="number"
              step="0.5"
              value={values.shoe_size ?? ''}
              disabled={!editable}
              onChange={e => setValues(v => ({ ...v, shoe_size: e.target.value }))}
              onBlur={e => editable && commitField('shoe_size', e.target.value, true)}
            />
            {canVerify && <SealButton state={sealState('shoe_size')} title={sealTitle('shoe_size')} onToggle={() => toggleVerify('shoe_size')} />}
            {errors.shoe_size && <div className="form-field__error">{errors.shoe_size}</div>}
          </EditorField>
          {/* Read-only on the profile card — this view isn't Edit mode, so the
              gear picks show as plain data (editing lives in Edit ▸
              Measurables, which reuses GEAR_SELECT_FIELDS). `hand_preference`
              starts a fresh grid row so it and `foot_preference` sit together
              on one line. */}
          {GEAR_SELECT_FIELDS.map(f => (
            <EditorField
              key={f.name}
              label={f.label}
              state={fieldState(f.name)}
              className={f.name === 'hand_preference' ? 'ae-field--rowstart' : ''}
            >
              <input className="ae-field__input" value={values[f.name] || '—'} disabled readOnly />
              {canVerify && <SealButton state={sealState(f.name)} title={sealTitle(f.name)} onToggle={() => toggleVerify(f.name)} />}
            </EditorField>
          ))}
        </div>
      </div>
    </div>
  );
}
